import type { ScenarioTimeline_ShipFragment } from '../../../graphql';
import type { PortCallData } from '../../core/Core';
import {
  getDefaultPortCostByType,
  type PortCostEstimateMaps,
} from '../../hooks/usePortCostEstimates';
import type { ScenarioLocation } from '../../../lib/planningData';
import { isPortCallEventTimeData } from '../../core/TimeData';

export type DefaultPortCostEstimates = {
  byName?: { id: number; currency: string; cost: number };
  byTypes?: {
    id: number;
    currency: string;
    cost: number;
    typeName: string;
  }[];
  byClass?: {
    id: number;
    currency: string;
    cost: number;
    typeName: string;
  };
  byId?: { cost: number };
};

export function getPortCostDefaults({
  portCostEstimateMaps,
  vessel,
  portCalls,
  locationById,
  locationIdsByName,
}: {
  portCostEstimateMaps: PortCostEstimateMaps;
  vessel: ScenarioTimeline_ShipFragment;
  portCalls: PortCallData[];
  locationById: Map<number, ScenarioLocation>;
  locationIdsByName: Map<string, number>;
}) {
  const vesselId = vessel.id;
  const typeIds = vessel?.vesselAttributesAssociationsByShipId.nodes ?? [];
  const vesselClass = vessel?.vesselAttributeByPrimaryAttributeId;

  const { defaultByTypeId, defaultByVesselId, defaultById } =
    portCostEstimateMaps;

  const portCostDefaultsByPort: Map<string, DefaultPortCostEstimates> =
    new Map();

  for (const pc of portCalls) {
    const location = locationById.get(pc.locationId);
    const portName = location?.name;
    if (!location || !portName) continue;

    const existingCost = portCostDefaultsByPort.get(portName);
    const updatedCost: DefaultPortCostEstimates = { ...existingCost };

    // Check for cost by vessel name
    const defByName = vesselId && defaultByVesselId.get(location.id, vesselId);
    if (defByName) {
      updatedCost.byName = {
        id: defByName.id,
        cost: defByName.cost,
        currency: defByName.currency,
      };
    }

    // Check for costs by vessel types
    for (const type of typeIds) {
      if (!type?.vesselAttributeId) continue;
      const defByType = getDefaultPortCostByType(
        defaultByTypeId,
        location.id,
        type.vesselAttributeId,
        locationIdsByName,
      );

      if (!defByType) continue;
      const existingTypes = updatedCost.byTypes ?? [];
      updatedCost.byTypes = [
        ...existingTypes,
        {
          ...defByType,
        },
      ];
    }

    // Check for cost by vessel class
    if (vesselClass) {
      const defByClass = getDefaultPortCostByType(
        defaultByTypeId,
        location.id,
        vesselClass.id,
        locationIdsByName,
      );
      if (defByClass) {
        updatedCost.byClass = {
          ...defByClass,
        };
      }
    }

    // Check for cost by port cost default ID
    const portCostDefaultId = pc?.tds.find(isPortCallEventTimeData)?.event
      ?.entry?.portCostDefaultId;
    if (portCostDefaultId) {
      const defById = defaultById.get(portCostDefaultId);
      if (defById) {
        updatedCost.byId = { cost: defById };
      }
    }

    if (Object.keys(updatedCost).length > 0) {
      portCostDefaultsByPort.set(portName, updatedCost);
    }
  }
  return portCostDefaultsByPort;
}
